import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { calculateProgress } from "../utils/calculateProgress.js";
import { PlaylistRecord, usePlaylistRecord } from "../Hooks/usePlaylistRecord.tsx";

export type PlaylistSongs = string[];

type QueueContextType = {
  primaryTimestamp: number;
  playlist: PlaylistSongs;
  queue: PlaylistSongs;
  playingIndex: number | null;
  currentVideoTitle: string;
  totalSongs: number;
  currentSongDuration: number;
  currentArtistName: string;
  currentSongName: string;
  isLoading: boolean;
  isLastVideo: boolean;
  incrementPlayingIndex: () => void;
} & PlaylistRecord;

const QueueContext = createContext<QueueContextType | undefined>(undefined);

type QueueProviderProps = {
  children: ReactNode;
};

export const QueueProvider: React.FC<QueueProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [primaryTimestamp, setPrimaryTimestamp] = useState<number>(0);

  const [playlist, setPlaylist] = useState<PlaylistSongs>([]);
  const [queue, setQueue] = useState<PlaylistSongs>([]);

  const [playingIndex, setPlayingIndex] = useState<number | null>(null);

  const {
    videoData,
    durationMap,
    durationInSeconds,
    playlistTimestamp,
    stationName,
  } = usePlaylistRecord();

  useEffect(() => {
    setIsLoading(true);
    if (!durationMap || !durationInSeconds) return;

    calculateProgress(durationMap, durationInSeconds, playlistTimestamp).then(
      (progress) => {
        setPrimaryTimestamp(progress.progress);
        setPlaylist([
          ...progress.queue.slice(progress.playingIndex, progress.queue.length),
          ...progress.queue.slice(0, progress.playingIndex),
        ]);

        setPlayingIndex(progress.playingIndex);
        setQueue(progress.queue);
        setIsLoading(false);
      }
    );
  }, [durationMap, durationInSeconds, playlistTimestamp]);

  const currentVideoTitle = useMemo(() => {
    if (playingIndex === null) return "";
    if (!queue[playingIndex]) return "";
    return videoData[queue[playingIndex]]?.videoTitle;
  }, [videoData, queue, playingIndex]);

  const currentSongDuration = useMemo(() => {
    if (playingIndex === null) return 0;
    if (!queue[playingIndex]) return 0;
    return videoData[queue[playingIndex]]?.durationInSeconds;
  }, [videoData, queue, playingIndex]);

  const currentArtistName = useMemo(() => {
    if (playingIndex === null) return "";
    if (!queue[playingIndex]) return "";
    return videoData[queue[playingIndex]]?.artistName;
  }, [videoData, queue, playingIndex]);

  const currentSongName = useMemo(() => {
    if (playingIndex === null) return "";
    if (!queue[playingIndex]) return "";
    return videoData[queue[playingIndex]]?.songName;
  }, [videoData, queue, playingIndex]);

  const isLastVideo = useMemo(() => {
    if (playingIndex === null) return false;
    return playingIndex === playlist.length - 1;
  }, [playingIndex, playlist]);

  const incrementPlayingIndex = useCallback(() => {
    setPlayingIndex((prev) => {
      if (prev === null) {
        return 0;
      }
      if (prev === playlist.length - 1) {
        return 0;
      } else {
        return prev + 1;
      }
    });
  }, [playlist.length]);

  return (
    <QueueContext.Provider
      value={{
        primaryTimestamp,
        playlist,
        queue,
        playingIndex,
        currentVideoTitle,
        incrementPlayingIndex,
        totalSongs: playlist.length,
        currentSongDuration,
        currentArtistName,
        currentSongName,
        videoData,
        durationMap,
        durationInSeconds,
        playlistTimestamp,
        stationName,
        isLoading,
        isLastVideo,
      }}
    >
      {children}
    </QueueContext.Provider>
  );
};

export const useQueue = () => {
  const ctx = useContext(QueueContext);
  if (ctx === undefined) {
    throw new Error("useQueue must be used within a QueueProvider");
  }
  return ctx;
};
